import React from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  displayPrice,
  isPriceVariationsEnabled,
  requireListingImage,
} from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney, unitDivisor, convertUnitToSubUnit } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { types as sdkTypes } from '../../util/sdkLoader';
import { getDeliveryFloor } from '../../util/shippingOptions';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ListingCardThumbnail,
  FavoriteButton,
} from '../../components';

import QuickAddButton from './QuickAddButton';
import css from './ListingCard.module.css';

const { Money } = sdkTypes;

// The card crops its photo to its own, slightly squarer portrait shape (4:5)
// rather than the listing image variant's ratio, so every card in a row lines
// up whatever the seller uploaded.
const CARD_IMAGE_WIDTH = 4;
const CARD_IMAGE_HEIGHT = 5;

// Sellers listing something that isn't a book, or who don't know the writer,
// often type a placeholder to get past the author field. The card shows no
// author rather than "N/A". Compared without case, spaces or punctuation, so
// "N/A", "n.a." and "-" all match.
const FILLER_AUTHORS = ['', 'na', 'none', 'unknown', 'notapplicable', 'nil'];
const isFillerAuthor = text =>
  FILLER_AUTHORS.includes(text.toLowerCase().replace(/[\s./\\?\-\u2013\u2014]/g, ''));

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

// Lucide's "truck" icon (ISC licence), inlined rather than adding the package.
const IconTruck = () => (
  <svg
    className={css.truck}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M10 17h4V5H2v12h3" />
    <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1" />
    <circle cx="7.5" cy="17.5" r="2.5" />
    <circle cx="17.5" cy="17.5" r="2.5" />
  </svg>
);

/**
 * The seller's photo and name, leading to their shop (profile page). The whole
 * card is already a link to the book and links can't nest, so this behaves
 * like a link (role, keyboard, click) without being an <a>.
 */
const SellerLink = props => {
  const { authorId, children, label } = props;
  const history = useHistory();
  const routes = useRouteConfiguration();

  if (!authorId) {
    return <span className={css.sellerLink}>{children}</span>;
  }

  const goToShop = e => {
    e.preventDefault();
    e.stopPropagation();
    history.push(createResourceLocatorString('ProfilePage', routes, { id: authorId }, {}));
  };

  return (
    <span
      className={classNames(css.sellerLink, css.sellerLinkActive)}
      role="link"
      tabIndex={0}
      aria-label={label}
      onClick={goToShop}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          goToShop(e);
        }
      }}
    >
      {children}
    </span>
  );
};

const PriceMaybe = props => {
  const { price, publicData, config, intl, listingTypeConfig } = props;
  const showPrice = displayPrice(listingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * It either renders the first image from the listing's images array with lazy loading,
 * or a stylized placeholder if images are disabled for the listing type.
 * Also wraps the image in a fixed aspect ratio container for consistent layout.
 * @component
 * @param {Object} props
 * @param {Object} props.currentListing listing entity with image data
 * @param {Function?} props.setActivePropsMaybe mouse enter/leave handlers for map highlighting
 * @param {string} props.title listing title for alt text
 * @param {string} props.renderSizes img/srcset size rules
 * @param {number} props.aspectWidth aspect ratio width
 * @param {number} props.aspectHeight aspect ratio height
 * @param {string} props.variantPrefix image variant prefix (e.g. "listing-card")
 * @param {boolean} props.showListingImage whether to show actual listing image or not
 * @param {Object?} props.style the background color for the listing card with no image
 * @returns {JSX.Element} listing image with fixed aspect ratio or fallback preview
 */
const ListingCardImage = props => {
  const {
    currentListing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    showListingImage,
    style,
  } = props;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  // Render the listing image only if listing images are enabled in the listing type
  return showListingImage ? (
    <AspectRatioWrapper
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <LazyImage
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  ) : (
    <ListingCardThumbnail
      style={style}
      listingTitle={title}
      className={css.aspectRatioWrapper}
      width={aspectWidth}
      height={aspectHeight}
      setActivePropsMaybe={setActivePropsMaybe}
    />
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);

  const author = ensureUser(listing.author);
  // profile.publicData is absent on a bare/ensured user, so this must not
  // destructure through it unguarded.
  const {
    displayName: authorName,
    abbreviatedName: authorInitials,
    publicData: authorPublicData,
  } = author.attributes?.profile || {};
  const shippingPreferences = authorPublicData?.shippingPreferences;
  const sellerImage = author.profileImage?.attributes?.variants
    ? author.profileImage
    : null;

  const {
    listingType,
    cardStyle,
    parcelSize,
    selfShippingPrice,
    book_type: bookFormat,
  } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const { variantPrefix = 'listing-card' } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  // Only the seller's own, enabled delivery options count, and the floor is
  // worked out the same way as on the listing page, so the card and the
  // listing page quote the same postage for a book.
  const { fromPrice: shippingPrice, hasFreeOption } = getDeliveryFloor({
    shippingPreferences: shippingPreferences || [],
    bookSize: parcelSize,
    bookFormat,
    selfShippingPrice,
  });
  const formattedShippingPrice =
    shippingPrice == null
      ? null
      : formatMoney(
          intl,
          new Money(
            convertUnitToSubUnit(shippingPrice, unitDivisor(config.currency)),
            config.currency
          )
        );

  // The book's writer, as entered by the seller or filled in by the ISBN lookup.
  // Not to be confused with `author` above, which is the selling user.
  const enteredAuthor = Array.isArray(publicData?.Author)
    ? publicData.Author.join(', ')
    : publicData?.Author;
  const bookAuthor =
    typeof enteredAuthor === 'string' && isFillerAuthor(enteredAuthor) ? null : enteredAuthor;

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <div className={css.seller}>
        {showAuthorInfo ? (
          <SellerLink
            authorId={author.id?.uuid}
            label={intl.formatMessage({ id: 'ListingCard.sellerShop' }, { authorName })}
          >
            {sellerImage ? (
              <ResponsiveImage
                rootClassName={css.sellerPhoto}
                alt=""
                image={sellerImage}
                variants={['square-small', 'square-small2x']}
                sizes="20px"
              />
            ) : (
              <span className={css.sellerInitials} aria-hidden="true">
                {authorInitials}
              </span>
            )}
            <span className={css.sellerName}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </span>
          </SellerLink>
        ) : null}
        <FavoriteButton
          rootClassName={css.favorite}
          iconClassName={css.favoriteIcon}
          listingId={id}
          listingAuthor={author}
          isVisible={true}
        />
      </div>

      <div className={css.imageArea}>
        <ListingCardImage
          renderSizes={renderSizes}
          title={title}
          currentListing={currentListing}
          config={config}
          setActivePropsMaybe={setActivePropsMaybe}
          aspectWidth={CARD_IMAGE_WIDTH}
          aspectHeight={CARD_IMAGE_HEIGHT}
          variantPrefix={variantPrefix}
          style={cardStyle}
          showListingImage={showListingImage}
        />
        <PriceMaybe
          price={price}
          publicData={publicData}
          config={config}
          intl={intl}
          listingTypeConfig={foundListingTypeConfig}
        />
        {formattedShippingPrice == null && !hasFreeOption ? null : (
          <span
            className={css.shipping}
            title={
              formattedShippingPrice
                ? intl.formatMessage(
                    { id: 'ListingCard.deliveryFrom' },
                    { price: formattedShippingPrice }
                  )
                : intl.formatMessage({ id: 'ListingCard.freeDeliveryOnly' })
            }
          >
            {formattedShippingPrice ? (
              <FormattedMessage
                id="ListingCard.shippingBadge"
                values={{ price: formattedShippingPrice }}
              />
            ) : (
              <FormattedMessage id="ListingCard.freeShippingBadge" />
            )}
            <IconTruck />
          </span>
        )}
      </div>

      <div className={css.info}>
        <div className={css.infoText}>
          {/* One line; longer titles end in "…" and show in full on hover. */}
          <h3 className={css.title} title={title}>
            {title}
          </h3>
          <span className={css.bookAuthor} title={bookAuthor}>
            {bookAuthor}
          </span>
        </div>
        <QuickAddButton listing={currentListing} />
      </div>
    </NamedLink>
  );
};

export default ListingCard;
